import { useEffect, useState, useCallback } from "react";
import { Wallet, Loader2, ArrowDownCircle, Lock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nProvider";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const CURRENCIES = ["EUR", "USD", "TRY", "GBP"];
const money = (n, c = "EUR") => new Intl.NumberFormat("en-IE", { style: "currency", currency: c }).format(n || 0);
const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }) : "—");

export default function CashDrawer() {
  const { user } = useAuth();
  const t = useT();
  const isOperator = ["translator_cashier", "owner"].includes(user?.role);
  const isReviewer = ["owner", "operations_manager", "general_accountant"].includes(user?.role);

  const [active, setActive] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState({ opening_cash: "", currency: "EUR" });
  const [outflow, setOutflow] = useState({ amount: "", reason: "", currency: "EUR" });
  const [actuals, setActuals] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const jobs = [];
    if (isOperator) jobs.push(api.get("/cash-shifts/me/active").then((r) => setActive(r.data.active ? r.data.shift : null)).catch(() => {}));
    jobs.push(api.get("/cash-shifts").then((r) => setShifts(Array.isArray(r.data) ? r.data : [])).catch(() => {}));
    Promise.all(jobs).finally(() => setLoading(false));
  }, [isOperator]);
  useEffect(() => { load(); }, [load]);

  const openShift = async () => {
    setBusy(true);
    try {
      await api.post("/cash-shifts/open", { opening_cash: Number(openForm.opening_cash || 0), currency: openForm.currency });
      toast.success(t("cash.shiftOpened"));
      setOpenForm({ opening_cash: "", currency: "EUR" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const addOutflow = async () => {
    setBusy(true);
    try {
      await api.post(`/cash-shifts/${active.id}/outflow`, { amount: Number(outflow.amount || 0), reason: outflow.reason, currency: outflow.currency });
      toast.success(t("cash.outflowRecorded"));
      setOutflow({ amount: "", reason: "", currency: active.primary_currency });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const activeCurrencies = (active?.currencies || []).filter(
    (b) => b.opening || b.cash_received || b.outflows_total || b.currency === active?.primary_currency
  );

  const closeShift = async () => {
    const actual_by_currency = {};
    for (const b of activeCurrencies) {
      const v = actuals[b.currency];
      if (v === undefined || v === "") { toast.error(`${t("cash.enterCounted")} ${b.currency}`); return; }
      actual_by_currency[b.currency] = Number(v);
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/cash-shifts/${active.id}/close`, { actual_by_currency });
      toast.success(`${t("cash.shiftClosed")} ${money(data.eur_consolidated.variance_eur, "EUR")}`);
      setActuals({});
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const vTone = (v) => (v == null ? "text-slate-300" : Math.abs(v) < 0.01 ? "text-emerald-600" : v > 0 ? "text-blue-600" : "text-red-600");

  return (
    <div className="p-6 sm:p-8 max-w-[1200px]">
      <PageHeader title={t("cash.title")} subtitle={t("cash.subtitle")} />

      {loading ? <div className="p-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-navy" /></div> : (
        <>
          {isOperator && (
            <div className="mb-6">
              {!active ? (
                <div className="bg-white rounded-xl border border-slate-200 p-5 max-w-md" data-testid="open-shift-card">
                  <h3 className="font-heading text-lg font-medium text-slate-900 mb-3 flex items-center gap-2"><Wallet className="h-5 w-5" /> {t("cash.openShift")}</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-slate-500">{t("cash.openingCash")}</label>
                      <Input type="number" min={0} step="0.01" value={openForm.opening_cash}
                        onChange={(e) => setOpenForm((f) => ({ ...f, opening_cash: e.target.value }))}
                        placeholder="0.00" data-testid="opening-cash-input" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500">{t("cash.primaryCurrency")}</label>
                      <Select value={openForm.currency} onValueChange={(v) => setOpenForm((f) => ({ ...f, currency: v }))}>
                        <SelectTrigger data-testid="opening-currency-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <Button onClick={openShift} disabled={busy} className="w-full bg-navy hover:bg-navy-soft text-white" data-testid="open-shift-button">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("cash.openShiftBtn")}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="active-shift-card">
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <h3 className="font-heading text-lg font-medium text-slate-900 flex items-center gap-2"><Wallet className="h-5 w-5" /> {t("cash.activeShift")}</h3>
                    <Badge className="bg-emerald-100 text-emerald-700">{t("cash.openSince")} {fmt(active.opening_time)}</Badge>
                  </div>

                  <div className="overflow-x-auto mb-3">
                    <table className="w-full text-sm" data-testid="active-currency-table">
                      <thead className="bg-slate-50 text-slate-500">
                        <tr className="text-left">
                          <th className="px-3 py-2 font-medium">{t("cash.currency")}</th>
                          <th className="px-3 py-2 font-medium">{t("cash.opening")}</th>
                          <th className="px-3 py-2 font-medium">{t("cash.cashReceived")}</th>
                          <th className="px-3 py-2 font-medium">{t("cash.cardInfo")}</th>
                          <th className="px-3 py-2 font-medium">{t("cash.outflows")}</th>
                          <th className="px-3 py-2 font-medium">{t("cash.expected")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {active.currencies.map((b) => (
                          <tr key={b.currency} className="border-t border-slate-50" data-testid={`active-currency-${b.currency}`}>
                            <td className="px-3 py-2 font-medium">{b.currency}</td>
                            <td className="px-3 py-2 tabular-nums">{money(b.opening, b.currency)}</td>
                            <td className="px-3 py-2 tabular-nums text-emerald-600" data-testid={`active-cash-${b.currency}`}>{money(b.cash_received, b.currency)}</td>
                            <td className="px-3 py-2 tabular-nums text-slate-400">{money(b.card_total, b.currency)}</td>
                            <td className="px-3 py-2 tabular-nums text-amber-600">{money(b.outflows_total, b.currency)}</td>
                            <td className="px-3 py-2 tabular-nums font-medium" data-testid={`active-expected-${b.currency}`}>{money(b.expected, b.currency)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-slate-500 mb-4" data-testid="eur-consolidated">{t("cash.consolidatedExpected")}: <b>{money(active.eur_consolidated.expected_eur, "EUR")}</b></p>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="rounded-lg border border-slate-100 p-4">
                      <p className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-1"><ArrowDownCircle className="h-4 w-4" /> {t("cash.recordOutflow")}</p>
                      <div className="flex gap-2">
                        <Input type="number" min={0} step="0.01" placeholder={t("cash.amount")} value={outflow.amount}
                          onChange={(e) => setOutflow((f) => ({ ...f, amount: e.target.value }))} data-testid="outflow-amount" className="w-24" />
                        <Select value={outflow.currency} onValueChange={(v) => setOutflow((f) => ({ ...f, currency: v }))}>
                          <SelectTrigger className="w-20" data-testid="outflow-currency"><SelectValue /></SelectTrigger>
                          <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input placeholder={t("cash.reason")} value={outflow.reason}
                          onChange={(e) => setOutflow((f) => ({ ...f, reason: e.target.value }))} data-testid="outflow-reason" />
                        <Button variant="outline" onClick={addOutflow} disabled={busy} data-testid="outflow-button">{t("common.add")}</Button>
                      </div>
                      {active.outflows.length > 0 && (
                        <ul className="mt-3 space-y-1 text-xs text-slate-500">
                          {active.outflows.map((o, i) => <li key={i}>{money(o.amount, o.currency)} · {o.reason} <span className="text-slate-300">({fmt(o.at)})</span></li>)}
                        </ul>
                      )}
                    </div>
                    <div className="rounded-lg border border-slate-100 p-4">
                      <p className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-1"><Lock className="h-4 w-4" /> {t("cash.closeReconcile")}</p>
                      <div className="space-y-2">
                        {activeCurrencies.map((b) => (
                          <div key={b.currency} className="flex items-center gap-2">
                            <span className="w-12 text-xs font-medium text-slate-600">{b.currency}</span>
                            <span className="text-xs text-slate-400 w-28">{t("cash.exp")} {money(b.expected, b.currency)}</span>
                            <Input type="number" min={0} step="0.01" placeholder={t("cash.counted")} value={actuals[b.currency] ?? ""}
                              onChange={(e) => setActuals((a) => ({ ...a, [b.currency]: e.target.value }))}
                              data-testid={`actual-${b.currency}`} />
                          </div>
                        ))}
                        <Button onClick={closeShift} disabled={busy} className="w-full bg-navy hover:bg-navy-soft text-white" data-testid="close-shift-button">
                          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("cash.closeShiftBtn")}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="shifts-table-card">
            <div className="px-5 py-3 border-b border-slate-100">
              <h3 className="font-heading text-lg font-medium text-slate-900">{isReviewer ? t("cash.allShifts") : t("cash.myShifts")}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="shifts-table">
                <thead className="bg-slate-50 text-slate-500">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-medium">{t("cash.cashier")}</th>
                    <th className="px-3 py-2 font-medium">{t("cash.opened")}</th>
                    <th className="px-3 py-2 font-medium">{t("cash.status")}</th>
                    <th className="px-3 py-2 font-medium">{t("cash.currencies")}</th>
                    <th className="px-3 py-2 font-medium">{t("cash.expectedEur")}</th>
                    <th className="px-3 py-2 font-medium">{t("cash.actualEur")}</th>
                    <th className="px-3 py-2 font-medium">{t("cash.varianceEur")}</th>
                  </tr>
                </thead>
                <tbody>
                  {shifts.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">{t("cash.noShifts")}</td></tr>}
                  {shifts.map((s) => (
                    <tr key={s.id} data-testid={`shift-row-${s.id}`} className="border-t border-slate-50 align-top">
                      <td className="px-3 py-2 font-medium text-slate-800">{s.cashier_name}</td>
                      <td className="px-3 py-2 text-slate-500">{fmt(s.opening_time)}</td>
                      <td className="px-3 py-2"><Badge className={s.status === "open" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}>{t(`cash.${s.status}`, s.status)}</Badge></td>
                      <td className="px-3 py-2 text-xs text-slate-500">
                        {s.currencies.filter((b) => b.opening || b.cash_received || b.outflows_total).map((b) => (
                          <div key={b.currency} data-testid={`shift-${s.id}-cur-${b.currency}`}>
                            {b.currency}: {t("cash.exp")} {money(b.expected, b.currency)}{b.variance != null ? ` · ${t("cash.var")} ${money(b.variance, b.currency)}` : ""}
                          </div>
                        ))}
                      </td>
                      <td className="px-3 py-2 tabular-nums">{money(s.eur_consolidated.expected_eur, "EUR")}</td>
                      <td className="px-3 py-2 tabular-nums">{s.status === "closed" ? money(s.eur_consolidated.actual_eur, "EUR") : "—"}</td>
                      <td className={`px-3 py-2 tabular-nums font-medium ${s.status === "closed" ? vTone(s.eur_consolidated.variance_eur) : "text-slate-300"}`} data-testid={`shift-variance-${s.id}`}>
                        {s.status === "closed" ? money(s.eur_consolidated.variance_eur, "EUR") : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
